import type { IOEPSeasonTimeline, OEPDraftOrSubmitted, OEPPhase } from 'agu_oep_core';
import type { AGUScaleOption } from 'agu_app_shell';
import { FirstCriterion, type OEPRubricLevelInput } from '../review/review.vm';
import { NominationDeadlineLine, NominationStage } from '../nominate/nominate.vm';
import { PresenterMeta } from '../shared/presenter';

/** The Outstanding level: needs a written justification (logic.js:313; agu_oep_core NominationResponseSummary counts NumericValue 5). */
export const OEP_OUTSTANDING = 5;

/** A rated nomination criterion (Review Questions FormType 'Nomination', metadata/review-questions/oep-nomination-form.json). */
export interface OEPNomQuestionInput { ID: string; Sequence: number; Text: string; Description: string | null; Levels: OEPRubricLevelInput[] }
/** One criterion as the form holds it: the picked RatingValue and, for Outstanding, its justification. */
export interface OEPNomAnswer { Level: number | null; Justification: string }
export type OEPNomAnswers = Readonly<Record<string, OEPNomAnswer>>;

export interface NominationInputs {
    Phase: OEPPhase;
    Timeline: IOEPSeasonTimeline;
    SeasonCode: string;
    Presentation: {
        ID: string; Title: string; SectionName: string; PresenterName: string;
        Institution: string | null; CareerStage: string | null; Format: string | null; Code: string | null;
    };
    Questions: OEPNomQuestionInput[];
    /** My nomination for this presentation this season; null before the first save. */
    Nomination: { ID: string; Status: OEPDraftOrSubmitted } | null;
    /** "Edit nomination" was pressed on a submitted nomination (in this session). */
    Editing: boolean;
}

export interface OEPNomRubricBlockVM { QuestionID: string; HeadingID: string; Name: string; Label: string; Help: string; Options: AGUScaleOption[] }

export type NominationMode = 'editing' | 'done';

export interface NominationVM {
    Mode: NominationMode;
    Title: string;
    Who: string;
    /** markup.html:714-718; CanChange adds "Edit nomination" and "Withdraw" while the window is open. */
    Done: { Title: string; Body: string; CanChange: boolean } | null;
    Rubric: OEPNomRubricBlockVM[];
}

/**
 * Submit is enabled only when the server would accept it — the same tests as DecideNominationChange
 * (packages/OEPCore/src/rules/decisions.ts:149-159): every criterion rated, every Outstanding
 * rating justified, and the conflict-of-interest attestation ticked.
 */
export function CanSubmitNomination(questions: readonly OEPNomQuestionInput[], answers: OEPNomAnswers, attested: boolean): boolean {
    return attested && questions.length > 0 && questions.every((q) => {
        const a = answers[q.ID];
        if (!a || a.Level === null) return false;
        return a.Level !== OEP_OUTSTANDING || a.Justification.trim().length > 0;
    });
}

function modeOf(i: NominationInputs): NominationMode {
    // Editing only while the Nomination window is open (DecideNominationChange); a submitted one
    // shows the done view until "Edit nomination" (logic.js:689-690, 697).
    if (NominationStage(i.Phase) !== 'open') return 'done';
    return i.Nomination?.Status === 'Submitted' && !i.Editing ? 'done' : 'editing';
}

/** logic.js:691-697 nomDoneTitle / nomDoneBody (task 27 substitutions: section, SelectionDue, NominationsClose, "next season"). */
function done(i: NominationInputs): NonNullable<NominationVM['Done']> {
    const d = i.Timeline.Dates;
    const submitted = i.Nomination?.Status === 'Submitted';
    const section = i.Presentation.SectionName;
    const stage = NominationStage(i.Phase);
    if (stage === 'pre') {
        // Reached only by a hand-edited link: the list's CTA (logic.js:346) and the callout line (logic.js:685).
        return { Title: `Opens at ${i.SeasonCode}`, Body: NominationDeadlineLine(i.Phase, i.SeasonCode, i.Timeline), CanChange: false };
    }
    if (stage === 'closed') {
        return {
            Title: 'Nominations are closed',
            Body: submitted
                ? `Your nomination went to the ${section} section committee, which selects winners by ${d.SelectionDue.Label}. It can no longer be edited.`
                : `This presentation was not nominated before the window closed on ${d.NominationsClose.Label}. Nominations reopen next season.`,
            CanChange: false,
        };
    }
    return {
        Title: 'Nomination submitted',
        Body: `It goes to the ${section} section committee in January. You can edit or withdraw it until nominations close on ${d.NominationsClose.Label}.`,
        CanChange: true,
    };
}

/** markup.html:711-760 / logic.js:304-320, 687-697 as one pure function. */
export function BuildNominationVM(i: NominationInputs): NominationVM {
    const mode = modeOf(i);
    return {
        Mode: mode,
        Title: i.Presentation.Title,
        // markup.html:723 "{{ nomWho }} · University of Colorado Boulder · Graduate student · Poster · Session: A23F-0891"
        Who: PresenterMeta(i.Presentation),
        Done: mode === 'done' ? done(i) : null,
        // markup.html:727-740, logic.js:304-320 buildRubric('nomScores', true): SCALE + 'Outstanding'
        Rubric: mode !== 'editing' ? [] : [...i.Questions].sort((x, y) => x.Sequence - y.Sequence).map((q) => ({
            QuestionID: q.ID,
            HeadingID: `oep-nq-${q.ID}-label`,
            Name: `oep-nq-${q.ID}`,
            Label: `${q.Sequence}. ${q.Text}`, // logic.js:31 "1. Demonstration of scientific reasoning"
            Help: q.Description ?? '',
            Options: [...q.Levels].sort((x, y) => x.Value - y.Value).map((l) => ({
                Value: l.Value,
                // logic.js:62 SCALE "Not evident (1)" … plus 'Outstanding' with no number (logic.js:307)
                Label: l.Value === OEP_OUTSTANDING ? l.Label : `${l.Label} (${l.Value})`,
                Description: [FirstCriterion(l.Criteria)],
            })),
        })),
    };
}
